import React, { useState, useEffect, useContext } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { unsetClient, setClient } from '../../actions/client';
import * as api from '../../utils/api';
import { ToastCtx } from '../../contexts/ToastContext';
import useAlert from '../../hooks/useModal';
import useRequireLogin from '../../hooks/useRequireLogin';
// Components
import AccountNav from '../../components/customerArea/shared/AccountNav';
import Row from '../../components/shared/atoms/Row';
import Container from '../../components/shared/atoms/Container';
import Box from '../../components/shared/atoms/Box';
import Heading from '../../components/shared/atoms/Heading';
import Text from '../../components/shared/atoms/Text';
import Button from '../../components/shared/atoms/Button';
import Separator from '../../components/Separator';
import Informations from '../../components/customerArea/myAccount/Informations';
import Password from '../../components/customerArea/myAccount/Password';
import Deconnexion from '../../components/customerArea/myAccount/Deconnexion';
import Sponsorship from '../../components/customerArea/myAppointments/Sponsorship';
import ShareSponsorshipModal from '../../components/customerArea/myAppointments/ShareSponsorshipModal';
import Trash24 from '../../components/icons/Trash24';

function MyAccount() {
  const { t } = useTranslation();
  const [updatePassword, setUpdatePassword] = useState(false);
  const [updateInformations, setUpdateInformations] = useState(false);
  const [referCode, setReferCode] = useState(null);
  const [codeExpiration, setCodeExpiration] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const { client } = useSelector((s) => s);
  const dispatch = useDispatch();
  const history = useHistory();
  const { showToast } = useContext(ToastCtx);
  const showModal = useAlert();
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const isLoggedIn = useRequireLogin();

  const getReferCode = async () => {
    if (!client.id) return;
    const { code, expiration } = await api.get(
      `/clients/${client.id}/referCode`,
    );
    setReferCode(code);
    setCodeExpiration(expiration);
  };

  const generateNewCode = async () => {
    setIsGenerating(true);
    try {
      const { code, expiration } = await api.post(
        `/clients/${client.id}/referCode`,
      );
      setReferCode(code);
      setCodeExpiration(expiration);
      showToast({
        type: 'success.main',
        title: t('sponsorship.success.generated', 'Code généré !'),
      });
    } catch (error) {
      showToast({
        type: 'error.main',
        title:
          error.message ||
          t('sponsorship.error.generation', 'Erreur lors de la génération'),
      });
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    getReferCode();
  }, [client.id]);

  const logout = () => {
    dispatch(unsetClient());
    history.push('/');
  };

  const deleteAccount = async () => {
    setIsDeletingAccount(true);
    try {
      const { status } = await api.deleteClient(client.id);
      if (status === 200 || status === 204) {
        showToast({
          type: 'success.main',
          title: t('account.delete.alerts.success'),
        });
        dispatch(unsetClient());
        window.location.assign('/');
        return;
      }
      if (status === 409) {
        showToast({
          type: 'error.main',
          title: t('account.delete.blocked.text'),
        });
      } else {
        showToast({
          type: 'error.main',
          title: t('account.delete.alerts.error'),
        });
      }
    } catch (error) {
      showToast({
        type: 'error.main',
        title: t('account.delete.alerts.error'),
      });
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const openDeleteAccountModal = () => {
    showModal({
      title: t('account.delete.modal.title'),
      content: t('account.delete.modal.text'),
      showModal: true,
      showButton: [
        {
          text: t('button.cancel'),
          variant: 'neutral',
        },
        {
          text: t('account.delete.modal.confirm'),
          color: 'primary.darkest',
          textColor: 'white',
          datatestid: 'confirm-delete-account',
          onClick: deleteAccount,
        },
      ],
    });
  };

  const submitPassword = async (
    values,
    { setSubmitting, setErrors, resetForm },
  ) => {
    setSubmitting(true);
    const { current_password, new_password } = values;
    const response = await api.put(`/clients/${client.id}`, {
      current_password,
      password: new_password,
    });
    if (response.id) {
      setUpdatePassword(true);
      resetForm({
        current_password: '',
        new_password: '',
        new_password_confirm: '',
      });
    } else {
      setErrors(response.errors || {});
    }
    setSubmitting(false);
  };

  const submitInformations = async (values, { setSubmitting, setErrors }) => {
    setSubmitting(true);
    const response = await api.put(`/clients/${client.id}`, values);
    if (response.id) {
      dispatch(setClient(client));
      setUpdateInformations(true);
    } else {
      setErrors(response.errors || {});
    }
    setSubmitting(false);
  };

  if (!isLoggedIn) {
    return null;
  }

  return (
    <Container>
      <Container fluid pb={10} pt={4}>
        <Helmet title={t('account.meta.title')} />
        <Row md gap={56}>
          <AccountNav />
          <Box>
            <Heading level={2}>{t('myaccount')}</Heading>
            <Informations
              client={client}
              submitInformations={submitInformations}
              updateInformations={updateInformations}
            />
            <Separator axe="horizontal" bg="gray.lighter" margin="40px 0px" />
            <Password
              submitPassword={submitPassword}
              updatePassword={updatePassword}
            />
            <Separator axe="horizontal" bg="gray.lighter" margin="40px 0px" />
            <Heading level={5} mb={4}>
              {t('sponsorship.heading', 'Parrainage')}
            </Heading>
            <Sponsorship
              referCode={referCode}
              codeExpiration={codeExpiration}
              onGenerateCode={generateNewCode}
              isGenerating={isGenerating}
              setShareModal={setShareModal}
            />
            <ShareSponsorshipModal
              open={shareModal}
              onRequestClose={() => setShareModal(false)}
              referCode={referCode}
            />
            {client.google_email && (
              <>
                <Separator
                  axe="horizontal"
                  bg="gray.lighter"
                  margin="40px 0px"
                />
                <Box mb={6}>
                  <Heading level={5}>
                    {t('googleauth.link.title', 'Connexion avec Google')}
                  </Heading>
                  <Text mt={2} mb={3} color="gray.darker">
                    {t('googleauth.link.connectedAs', {
                      defaultValue: 'Compte Google lié : {{email}}',
                      email: client.google_email,
                    })}
                  </Text>
                </Box>
              </>
            )}
            <Separator axe="horizontal" bg="gray.lighter" margin="40px 0px" />
            <Deconnexion logout={logout} />
            <Separator axe="horizontal" bg="gray.lighter" margin="40px 0px" />
            <Box
              justifyContent="space-between"
              flexDirection={{ xs: 'column', sm: 'row' }}>
              <Box mb={{ xs: 4, sm: 0 }}>
                <Heading level={5}>{t('account.delete.heading')}</Heading>
                <Text>{t('account.delete.description')}</Text>
              </Box>
              <Button
                before={<Trash24 />}
                variant="neutral"
                color="primary.darkest"
                disabled={isDeletingAccount}
                data-testid="delete-account-cta"
                onClick={openDeleteAccountModal}>
                {t('account.delete.cta')}
              </Button>
            </Box>
          </Box>
        </Row>
      </Container>
    </Container>
  );
}

export default MyAccount;
